import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { staffProfileAPI, jobCodeAPI, staffAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import { useAuth } from "../AuthContext";
import {
  EMPLOYMENT_TYPES,
  formatCentsAsCurrency,
  parseCurrencyToCents,
  isAdminRole,
  toDateInputValue,
} from "../../utils/workforceUtils";
import StaffLayout from "./StaffLayout";
import "./StaffProfileForm.css";

// TIME-001-polish-ux-001 §AC-5: weekly-hour-limit tooltip copy.
// Sourced from data-models.md §17.1 "weeklyHourLimit" field description
// (per-staff weekly cap for scheduling conflict detection / overtime warnings).
// Wording mirrors the story file's Scenario 5 verbatim.
const WEEKLY_HOUR_LIMIT_TOOLTIP =
  "Weekly hour ceiling that gates overtime warnings (PAY-002) and schedule-publish (SCHED-005). Set 40 for full-time W-2; leave blank for 1099 contractors who self-cap.";

const EMPTY_FORM = {
  userId: "",
  firstName: "",
  lastName: "",
  email: "",
  contactNumber: "",
  employmentType: "",
  primaryRole: "",
  startDate: "",
  endDate: "",
  weeklyHourLimit: "",
  hourlyRate: "", // dollars-string for the input; converted to cents on save
  hourlyRateEffectiveDate: "",
  payrollVendorRef: "",
};

/**
 * Workforce — Staff profile form (level 2 of TIME-001 spec).
 *
 * Handles create + edit modes:
 *  - Create:  /staff/staff-profiles/new
 *  - Edit:    /staff/staff-profiles/:id
 *
 * Includes:
 *  - User picker (search existing User by email/name) for the staff-as-member case
 *  - Rate-edit modal (Scenario 2: appends new effective-dated rate, never overwrites)
 *  - Deactivate confirmation modal (Scenario 5: soft-deactivate)
 *  - Inline rate-history list (admin-only; queries AuditLog for category=workforce,
 *    action=staff_rate_change per data-models §17.5)
 *  - RBAC: hourlyRate fields hidden for non-admin viewers
 */
const StaffProfileForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);
  const adminAccess = isAdminRole(user?.role);

  const [form, setForm] = useState(EMPTY_FORM);
  const [original, setOriginal] = useState(null); // server snapshot for edit mode
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [jobCodes, setJobCodes] = useState([]);
  const [rateHistory, setRateHistory] = useState([]);
  const [rateHistoryLoading, setRateHistoryLoading] = useState(false);

  // User picker state
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [userSearchInFlight, setUserSearchInFlight] = useState(false);
  const userSearchTimer = useRef(null);

  // Modals
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);

  // TIME-001-polish-ux-001 §AC-1: inline "+ Create new user account" modal
  // (replaces the "go to Customer Management → create → return" friction).
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
  });
  const [createUserSaving, setCreateUserSaving] = useState(false);
  const [createUserError, setCreateUserError] = useState("");

  // TIME-001-polish-ux-001 §AC-7: cancel-with-dirty-form guard.
  // Snapshot the form state we last considered "saved" — start at EMPTY_FORM
  // for create mode, and at server-loaded values for edit mode (set by
  // loadStaffProfile below). Deep-equal on cancel/back; if mismatch, prompt.
  const [pristineForm, setPristineForm] = useState(EMPTY_FORM);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);

  // ------- Loaders -------

  const loadJobCodes = useCallback(async () => {
    try {
      const response = await jobCodeAPI.list({ active: true });
      const list =
        response.data?.jobCodes ||
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : []);
      setJobCodes(list);
    } catch (err) {
      // Job codes failing-to-load is non-blocking; field falls back to free text.
      handleApiError(
        err,
        "StaffProfileForm.jobCodes",
        () => {},
        "Could not load job codes; you can still type a role."
      );
    }
  }, []);

  const loadRateHistory = useCallback(async (staffProfileId) => {
    if (!staffProfileId) return;
    try {
      setRateHistoryLoading(true);
      const response = await staffProfileAPI.getRateHistory(staffProfileId);
      const list =
        response.data?.entries ||
        response.data?.auditEntries ||
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : []);
      setRateHistory(list);
    } catch (err) {
      // Non-blocking — rate history is supplementary context.
      setRateHistory([]);
    } finally {
      setRateHistoryLoading(false);
    }
  }, []);

  const loadStaffProfile = useCallback(async () => {
    if (!isEdit) return;
    try {
      setLoading(true);
      const response = await staffProfileAPI.getById(id);
      const sp =
        response.data?.profile ||
        response.data?.staffProfile ||
        response.data?.data ||
        response.data;
      if (!sp) {
        setError("Staff profile not found.");
        return;
      }
      setOriginal(sp);
      // Backend `populate("userId", ...)` returns userId as an embedded
      // User object — unwrap for identity fields and pull the raw _id back
      // out for the form's userId state.
      const linkedUser =
        sp.userId && typeof sp.userId === "object" ? sp.userId : null;
      const userIdStr = linkedUser
        ? linkedUser._id || linkedUser.id || ""
        : sp.userId || sp.user?._id || sp.user?.id || "";
      const next = {
        userId: userIdStr,
        firstName: linkedUser?.firstName || sp.firstName || sp.user?.firstName || "",
        lastName: linkedUser?.lastName || sp.lastName || sp.user?.lastName || "",
        email: linkedUser?.email || sp.email || sp.user?.email || "",
        contactNumber:
          linkedUser?.contactNumber ||
          sp.contactNumber ||
          sp.user?.contactNumber ||
          "",
        employmentType: sp.employmentType || "",
        primaryRole: sp.primaryRole || "",
        startDate: toDateInputValue(sp.startDate),
        endDate: toDateInputValue(sp.endDate),
        weeklyHourLimit:
          sp.weeklyHourLimit === null || sp.weeklyHourLimit === undefined
            ? ""
            : String(sp.weeklyHourLimit),
        hourlyRate: formatCentsAsCurrency(sp.hourlyRate).replace("$", ""),
        hourlyRateEffectiveDate: toDateInputValue(sp.hourlyRateEffectiveDate),
        payrollVendorRef: sp.payrollVendorRef || "",
      };
      setForm(next);
      // TIME-001-polish-ux-001 §AC-7: capture the as-loaded snapshot so
      // Cancel/back can detect dirty state.
      setPristineForm(next);
      setError("");
    } catch (err) {
      handleApiError(
        err,
        "StaffProfileForm.fetch",
        setError,
        "Error loading staff profile."
      );
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (!adminAccess) return;
    loadJobCodes();
    if (isEdit) {
      loadStaffProfile();
      if (adminAccess) {
        loadRateHistory(id);
      }
    }
  }, [adminAccess, isEdit, id, loadJobCodes, loadStaffProfile, loadRateHistory]);

  // ------- User picker (debounced search against existing customer-search endpoint) -------

  useEffect(() => {
    if (isEdit) return; // user picker only relevant in create mode
    if (!userQuery.trim() || userQuery.trim().length < 2) {
      setUserResults([]);
      return;
    }
    if (userSearchTimer.current) clearTimeout(userSearchTimer.current);
    userSearchTimer.current = setTimeout(async () => {
      try {
        setUserSearchInFlight(true);
        // Workforce-scoped picker — returns Users of ANY role so that
        // existing staff/admin Users can be linked, not just members.
        // Backed by GET /api/workforce/staff/users/search.
        const response = await staffProfileAPI.searchUsers(userQuery, 10);
        const list =
          response.data?.users ||
          response.data?.customers ||
          response.data?.data ||
          (Array.isArray(response.data) ? response.data : []);
        setUserResults(list);
      } catch (err) {
        // Soft-fail — picker just stays empty.
        setUserResults([]);
      } finally {
        setUserSearchInFlight(false);
      }
    }, 300);
    return () => {
      if (userSearchTimer.current) clearTimeout(userSearchTimer.current);
    };
  }, [userQuery, isEdit]);

  const selectExistingUser = (existing) => {
    setForm((prev) => ({
      ...prev,
      userId: existing._id || existing.id || "",
      firstName: existing.firstName || prev.firstName,
      lastName: existing.lastName || prev.lastName,
      email: existing.email || prev.email,
      contactNumber: existing.contactNumber || prev.contactNumber,
    }));
    setUserPickerOpen(false);
    setUserQuery("");
    setUserResults([]);
  };

  const clearLinkedUser = () => {
    setForm((prev) => ({ ...prev, userId: "" }));
  };

  // TIME-001-polish-ux-001 §AC-1: inline "+ Create new user account" flow.
  // Opens the create-user modal with whatever the admin has already typed
  // into the picker / form (email defaults to userQuery if it looks like
  // an email; otherwise blank). On save, creates a User via the existing
  // staffAPI.createAccount endpoint and auto-links the new userId to the
  // form, then closes the modal. Form state preserved end-to-end.
  const openCreateUserModal = () => {
    const looksLikeEmail = /@/.test(userQuery) && userQuery.includes(".");
    setCreateUserForm({
      firstName: form.firstName || "",
      lastName: form.lastName || "",
      email: looksLikeEmail ? userQuery.trim() : form.email || "",
      contactNumber: form.contactNumber || "",
    });
    setCreateUserError("");
    setCreateUserModalOpen(true);
  };

  const submitCreateUser = async (e) => {
    e.preventDefault();
    setCreateUserError("");
    if (!createUserForm.firstName.trim()) {
      setCreateUserError("First name is required.");
      return;
    }
    if (!createUserForm.lastName.trim()) {
      setCreateUserError("Last name is required.");
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createUserForm.email);
    if (!emailOk) {
      setCreateUserError("Valid email is required.");
      return;
    }
    try {
      setCreateUserSaving(true);
      const response = await staffAPI.createAccount({
        firstName: createUserForm.firstName.trim(),
        lastName: createUserForm.lastName.trim(),
        email: createUserForm.email.trim().toLowerCase(),
        contactNumber: createUserForm.contactNumber.trim() || undefined,
        // Default new accounts created from the StaffProfile picker to the
        // generic 'member' role — admin can elevate later via Customer
        // Management. Same default CreateAccount.js uses.
        role: "member",
      });
      const account = response.data?.account || response.data;
      const newUserId = account?._id || account?.id || account?.userId;
      if (!newUserId) {
        setCreateUserError(
          "Account was created but the linked user id was not returned. Refresh and try linking manually."
        );
        return;
      }
      // Link the new user into the form. Mirrors selectExistingUser shape.
      setForm((prev) => ({
        ...prev,
        userId: newUserId,
        firstName: createUserForm.firstName.trim() || prev.firstName,
        lastName: createUserForm.lastName.trim() || prev.lastName,
        email:
          createUserForm.email.trim().toLowerCase() || prev.email,
        contactNumber: createUserForm.contactNumber.trim() || prev.contactNumber,
      }));
      setCreateUserModalOpen(false);
      setUserPickerOpen(false);
      setUserQuery("");
      setUserResults([]);
      toast.success("New user account created and linked.");
    } catch (err) {
      if (err?.response?.status === 409) {
        setCreateUserError(
          err.response?.data?.message ||
            "A user with that email already exists. Search for them in the picker instead."
        );
      } else {
        handleApiError(
          err,
          "StaffProfileForm.createUser",
          setCreateUserError,
          "Could not create user account."
        );
      }
    } finally {
      setCreateUserSaving(false);
    }
  };

  // ------- Field handlers -------

  const handleField = (key) => (e) => {
    const value = e.target ? e.target.value : e;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // TIME-001-polish-ux-001 §AC-2: when admin sets `startDate` and the
      // hourlyRateEffectiveDate has not been touched yet, auto-fill it with
      // the same value. Admin can override afterwards. Only applies in
      // create mode (edit mode uses the rate-edit modal which has its own
      // effective-date field).
      if (
        !isEdit &&
        key === "startDate" &&
        value &&
        !prev.hourlyRateEffectiveDate
      ) {
        next.hourlyRateEffectiveDate = value;
      }
      return next;
    });
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // TIME-001-polish-ux-001 §AC-7: dirty-state detection. Deep-equal of
  // form against the pristine snapshot. Empty-string vs undefined is
  // normalized — both treated as "blank".
  const isFormDirty = useCallback(() => {
    const keys = Object.keys(EMPTY_FORM);
    return keys.some((k) => {
      const a = form[k] ?? "";
      const b = pristineForm[k] ?? "";
      return String(a) !== String(b);
    });
  }, [form, pristineForm]);

  // TIME-001-polish-ux-001 §AC-3: live "$X.YY/hour" preview computed
  // from the typed dollars-string. Returns empty string for invalid /
  // empty input so the preview stays hidden until admin types something
  // parseable.
  const liveHourlyRatePreview = (() => {
    const cents = parseCurrencyToCents(form.hourlyRate);
    if (cents === null || cents <= 0) return "";
    return formatCentsAsCurrency(cents);
  })();

  const validate = () => {
    const errs = {};
    if (!isEdit && !form.userId) {
      // Per TIME-001-polish-ux-001 §AC-1, admin can now create a new user
      // inline via the picker — no more navigate-away friction.
      errs.userId =
        "Link to an existing user, or create a new user account from the picker.";
    }
    if (!form.firstName.trim()) errs.firstName = "First name is required.";
    if (!form.lastName.trim()) errs.lastName = "Last name is required.";
    if (!form.email.trim()) errs.email = "Email is required.";
    if (!form.employmentType) errs.employmentType = "Employment type is required.";
    if (!form.primaryRole.trim()) errs.primaryRole = "Primary role is required.";
    if (!form.startDate) errs.startDate = "Start date is required.";
    if (!isEdit) {
      // Hourly rate is required at create time per backlog Scenario 4.
      const cents = parseCurrencyToCents(form.hourlyRate);
      if (cents === null || cents <= 0) {
        errs.hourlyRate = "Hourly rate is required.";
      }
      if (!form.hourlyRateEffectiveDate) {
        errs.hourlyRateEffectiveDate = "Effective date is required.";
      }
    }
    if (form.weeklyHourLimit) {
      const n = Number(form.weeklyHourLimit);
      if (Number.isNaN(n) || n < 0 || n > 168) {
        errs.weeklyHourLimit = "Weekly hour limit must be between 0 and 168.";
      }
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ------- Save (create or edit) -------

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim().toLowerCase(),
      contactNumber: form.contactNumber.trim() || undefined,
      employmentType: form.employmentType,
      primaryRole: form.primaryRole.trim(),
      startDate: form.startDate,
      endDate: form.endDate || null,
      weeklyHourLimit: form.weeklyHourLimit ? Number(form.weeklyHourLimit) : null,
      payrollVendorRef: form.payrollVendorRef.trim() || null,
    };
    if (form.userId) {
      payload.userId = form.userId;
    }
    if (!isEdit) {
      payload.hourlyRate = parseCurrencyToCents(form.hourlyRate);
      payload.hourlyRateEffectiveDate = form.hourlyRateEffectiveDate;
    }
    try {
      setSaving(true);
      if (isEdit) {
        const response = await staffProfileAPI.update(id, payload);
        const sp =
          response.data?.profile ||
          response.data?.staffProfile ||
          response.data?.data ||
          response.data;
        setOriginal(sp);
        // TIME-001-polish-ux-001 §AC-7: clear pristine baseline so subsequent
        // Cancel does not flag the just-saved state as dirty.
        setPristineForm({ ...form });
        // TIME-001-polish-ux-001 §AC-6: success toast on Save (edit path).
        const fullName = `${form.firstName} ${form.lastName}`.trim() || "Staff member";
        toast.success(`Profile updated for ${fullName}.`);
        setError("");
      } else {
        const response = await staffProfileAPI.create(payload);
        const sp =
          response.data?.profile ||
          response.data?.staffProfile ||
          response.data?.data ||
          response.data;
        const newId = sp?._id || sp?.id;
        // TIME-001-polish-ux-001 §AC-6: success toast on Save (create path).
        // Toast paints first; navigation runs on next microtask so the toast
        // has time to mount in the layout root before the staff-list page
        // unmounts the form's render tree (mitigates the toast-vs-redirect
        // race called out in story §Risks item 2). react-toastify renders to
        // ToastContainer mounted in App.js so it survives navigation, but the
        // setTimeout(0) keeps the order deterministic for QA.
        const fullName =
          `${form.firstName} ${form.lastName}`.trim() || "Staff member";
        toast.success(`${fullName} added to staff.`);
        // Pristine cleared before navigation so the unmount doesn't trigger
        // a stray dirty-form prompt.
        setPristineForm({ ...form });
        setTimeout(() => {
          if (newId) {
            navigate(`/staff/staff-profiles/${newId}`, { replace: true });
          } else {
            navigate("/staff/staff-profiles");
          }
        }, 0);
      }
    } catch (err) {
      handleApiError(
        err,
        "StaffProfileForm.save",
        setError,
        "Error saving staff profile."
      );
    } finally {
      setSaving(false);
    }
  };

  // ------- Rate-change modal (edit mode only) -------

  const [rateModalForm, setRateModalForm] = useState({
    newRate: "",
    effectiveDate: "",
    reason: "",
  });
  const [rateModalError, setRateModalError] = useState("");
  const [rateModalSaving, setRateModalSaving] = useState(false);

  const openRateModal = () => {
    setRateModalForm({
      newRate: "",
      effectiveDate: toDateInputValue(new Date()),
      reason: "",
    });
    setRateModalError("");
    setRateModalOpen(true);
  };

  const submitRateChange = async (e) => {
    e.preventDefault();
    const cents = parseCurrencyToCents(rateModalForm.newRate);
    if (cents === null || cents <= 0) {
      setRateModalError("Enter a valid hourly rate.");
      return;
    }
    if (!rateModalForm.effectiveDate) {
      setRateModalError("Choose an effective date.");
      return;
    }
    if (rateModalForm.reason.trim().length < 10) {
      setRateModalError("Reason must be at least 10 characters.");
      return;
    }
    try {
      setRateModalSaving(true);
      // Send as a partial update — backend writes an AuditLog row with
      // metadata.{oldRateCents, newRateCents, oldEffectiveDate,
      // newEffectiveDate, reason} per data-models §17.5 + Simone's
      // 2026-04-28 reconciliation. Backend reads body.reason (not
      // rateChangeReason).
      const response = await staffProfileAPI.update(id, {
        hourlyRate: cents,
        hourlyRateEffectiveDate: rateModalForm.effectiveDate,
        reason: rateModalForm.reason.trim(),
      });
      const sp =
        response.data?.profile ||
        response.data?.staffProfile ||
        response.data?.data ||
        response.data;
      if (sp) {
        setOriginal(sp);
        setForm((prev) => ({
          ...prev,
          hourlyRate: formatCentsAsCurrency(sp.hourlyRate).replace("$", ""),
          hourlyRateEffectiveDate: toDateInputValue(sp.hourlyRateEffectiveDate),
        }));
      }
      setRateModalOpen(false);
      // Re-pull rate history to show the new entry inline.
      loadRateHistory(id);
    } catch (err) {
      handleApiError(
        err,
        "StaffProfileForm.rateChange",
        setRateModalError,
        "Error saving rate change."
      );
    } finally {
      setRateModalSaving(false);
    }
  };

  // ------- Deactivate modal -------

  const [deactivateSaving, setDeactivateSaving] = useState(false);
  const [deactivateError, setDeactivateError] = useState("");

  const submitDeactivate = async () => {
    try {
      setDeactivateSaving(true);
      await staffProfileAPI.deactivate(id);
      setDeactivateModalOpen(false);
      navigate("/staff/staff-profiles");
    } catch (err) {
      handleApiError(
        err,
        "StaffProfileForm.deactivate",
        setDeactivateError,
        "Error deactivating staff profile."
      );
    } finally {
      setDeactivateSaving(false);
    }
  };

  // ------- Render -------

  if (!adminAccess) {
    return (
      <StaffLayout>
        <div className="staffprofile-form-container">
          <h1>Staff Profile</h1>
          <p className="staffprofile-rbac-warning" role="alert">
            You do not have permission to view or edit staff profiles. Contact
            an administrator if you need access.
          </p>
        </div>
      </StaffLayout>
    );
  }

  if (loading) {
    return (
      <StaffLayout>
        <div className="staffprofile-form-container">
          <p className="staffprofile-loading">Loading staff profile...</p>
        </div>
      </StaffLayout>
    );
  }

  const status = original?.active === false ? "inactive" : "active";

  return (
    <StaffLayout>
      <div className="staffprofile-form-container">
        <header className="staffprofile-form-header">
          <div>
            <Link to="/staff/staff-profiles" className="staffprofile-breadcrumb">
              ← Workforce / Staff
            </Link>
            <h1>
              {isEdit
                ? `${form.firstName} ${form.lastName}`.trim() || "Edit staff profile"
                : "Add new staff member"}
            </h1>
            {isEdit && (
              <span
                className={`staffprofile-status-pill staffprofile-status-${status}`}
              >
                {status === "active" ? "Active" : "Inactive"}
              </span>
            )}
          </div>
        </header>

        {error && (
          <div className="staffprofile-error" role="alert">
            {error}
          </div>
        )}

        <form className="staffprofile-form" onSubmit={handleSave} noValidate>
          {/* Identity */}
          <section className="staffprofile-card" aria-labelledby="identity-heading">
            <h2 id="identity-heading">Identity</h2>

            {!isEdit && (
              <div className="staffprofile-userpicker">
                <button
                  type="button"
                  className="staffprofile-btn staffprofile-btn-secondary"
                  onClick={() => setUserPickerOpen((v) => !v)}
                >
                  {form.userId ? "Linked to existing user" : "Link to existing user"}
                </button>
                {form.userId && (
                  <button
                    type="button"
                    className="staffprofile-link-btn"
                    onClick={clearLinkedUser}
                  >
                    Clear link
                  </button>
                )}
                {userPickerOpen && (
                  <div className="staffprofile-userpicker-panel">
                    <input
                      type="search"
                      placeholder="Search by name or email"
                      value={userQuery}
                      onChange={(e) => setUserQuery(e.target.value)}
                      className="staffprofile-input"
                      aria-label="Search existing users"
                    />
                    {userSearchInFlight && (
                      <p className="staffprofile-helper">Searching...</p>
                    )}
                    {!userSearchInFlight && userResults.length > 0 && (
                      <ul className="staffprofile-userpicker-results">
                        {userResults.map((u) => (
                          <li key={u._id || u.id}>
                            <button
                              type="button"
                              onClick={() => selectExistingUser(u)}
                              className="staffprofile-userpicker-result"
                            >
                              <strong>
                                {u.firstName} {u.lastName}
                              </strong>
                              <span>{u.email}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {!userSearchInFlight &&
                      userQuery.trim().length >= 2 &&
                      userResults.length === 0 && (
                        <div className="staffprofile-userpicker-empty">
                          <p className="staffprofile-helper">
                            No matching users.
                          </p>
                          {/* TIME-001-polish-ux-001 §AC-1: inline create CTA
                              replaces the prior "go to Customer Management"
                              two-step onboarding friction. */}
                          <button
                            type="button"
                            className="staffprofile-link-btn staffprofile-userpicker-create-cta"
                            onClick={openCreateUserModal}
                            data-testid="userpicker-create-cta"
                          >
                            + Create new user account
                            {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                              userQuery.trim()
                            )
                              ? ` for ${userQuery.trim()}`
                              : ""}
                          </button>
                        </div>
                      )}
                  </div>
                )}
                {form.userId && (
                  <p className="staffprofile-helper">
                    Linked to existing user. First name, last name, and email
                    are sourced from the user record.
                  </p>
                )}
                {fieldErrors.userId && (
                  <p
                    className="staffprofile-error-text"
                    role="alert"
                  >
                    {fieldErrors.userId}
                  </p>
                )}
              </div>
            )}

            <div className="staffprofile-grid-2">
              <div className="staffprofile-field">
                <label htmlFor="firstName">
                  First name<span aria-hidden="true">*</span>
                </label>
                <input
                  id="firstName"
                  type="text"
                  className="staffprofile-input"
                  value={form.firstName}
                  onChange={handleField("firstName")}
                  readOnly={Boolean(form.userId)}
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.firstName)}
                />
                {fieldErrors.firstName && (
                  <p className="staffprofile-error-text">{fieldErrors.firstName}</p>
                )}
              </div>

              <div className="staffprofile-field">
                <label htmlFor="lastName">
                  Last name<span aria-hidden="true">*</span>
                </label>
                <input
                  id="lastName"
                  type="text"
                  className="staffprofile-input"
                  value={form.lastName}
                  onChange={handleField("lastName")}
                  readOnly={Boolean(form.userId)}
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.lastName)}
                />
                {fieldErrors.lastName && (
                  <p className="staffprofile-error-text">{fieldErrors.lastName}</p>
                )}
              </div>
            </div>

            <div className="staffprofile-grid-2">
              <div className="staffprofile-field">
                <label htmlFor="email">
                  Email<span aria-hidden="true">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className="staffprofile-input"
                  value={form.email}
                  onChange={handleField("email")}
                  readOnly={Boolean(form.userId)}
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.email)}
                />
                {fieldErrors.email && (
                  <p className="staffprofile-error-text">{fieldErrors.email}</p>
                )}
              </div>

              <div className="staffprofile-field">
                <label htmlFor="contactNumber">Contact phone</label>
                <input
                  id="contactNumber"
                  type="tel"
                  className="staffprofile-input"
                  value={form.contactNumber}
                  onChange={handleField("contactNumber")}
                />
              </div>
            </div>
          </section>

          {/* Employment */}
          <section className="staffprofile-card" aria-labelledby="employment-heading">
            <h2 id="employment-heading">Employment</h2>

            <div className="staffprofile-grid-2">
              <div className="staffprofile-field">
                <label htmlFor="employmentType">
                  Employment type<span aria-hidden="true">*</span>
                </label>
                <select
                  id="employmentType"
                  className="staffprofile-input"
                  value={form.employmentType}
                  onChange={handleField("employmentType")}
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.employmentType)}
                >
                  <option value="">Select employment type</option>
                  {EMPLOYMENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                {form.employmentType && (
                  <p className="staffprofile-helper">
                    {EMPLOYMENT_TYPES.find((t) => t.value === form.employmentType)?.helper}
                  </p>
                )}
                {fieldErrors.employmentType && (
                  <p className="staffprofile-error-text">{fieldErrors.employmentType}</p>
                )}
              </div>

              <div className="staffprofile-field">
                <label htmlFor="primaryRole">
                  Primary role<span aria-hidden="true">*</span>
                </label>
                {jobCodes.length > 0 ? (
                  <input
                    id="primaryRole"
                    type="text"
                    list="staffprofile-jobcodes"
                    className="staffprofile-input"
                    value={form.primaryRole}
                    onChange={handleField("primaryRole")}
                    placeholder="e.g. front_desk"
                    aria-required="true"
                    aria-invalid={Boolean(fieldErrors.primaryRole)}
                  />
                ) : (
                  <input
                    id="primaryRole"
                    type="text"
                    className="staffprofile-input"
                    value={form.primaryRole}
                    onChange={handleField("primaryRole")}
                    placeholder="e.g. front_desk"
                    aria-required="true"
                    aria-invalid={Boolean(fieldErrors.primaryRole)}
                  />
                )}
                <datalist id="staffprofile-jobcodes">
                  {jobCodes.map((jc) => (
                    <option
                      key={jc._id || jc.id}
                      value={jc.label || jc.name || ""}
                    />
                  ))}
                </datalist>
                {fieldErrors.primaryRole && (
                  <p className="staffprofile-error-text">{fieldErrors.primaryRole}</p>
                )}
              </div>
            </div>

            <div className="staffprofile-grid-3">
              <div className="staffprofile-field">
                <label htmlFor="startDate">
                  Start date<span aria-hidden="true">*</span>
                </label>
                <input
                  id="startDate"
                  type="date"
                  className="staffprofile-input"
                  value={form.startDate}
                  onChange={handleField("startDate")}
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.startDate)}
                />
                {fieldErrors.startDate && (
                  <p className="staffprofile-error-text">{fieldErrors.startDate}</p>
                )}
              </div>
              <div className="staffprofile-field">
                <label htmlFor="endDate">End date</label>
                <input
                  id="endDate"
                  type="date"
                  className="staffprofile-input"
                  value={form.endDate}
                  onChange={handleField("endDate")}
                />
              </div>
              <div className="staffprofile-field">
                {/* TIME-001-polish-ux-001 §AC-5: weekly-hour-limit tooltip.
                    Hover (desktop) / focus (keyboard) reveals the gating
                    description sourced from the story file Scenario 5 +
                    data-models §17.1 weeklyHourLimit field doc. */}
                <label htmlFor="weeklyHourLimit" className="staffprofile-label-with-tip">
                  Weekly hour limit
                  <span
                    className="staffprofile-tooltip"
                    tabIndex={0}
                    role="img"
                    aria-label={`Weekly hour limit help: ${WEEKLY_HOUR_LIMIT_TOOLTIP}`}
                    data-testid="weeklyHourLimit-tooltip"
                  >
                    <span className="staffprofile-tooltip-glyph" aria-hidden="true">
                      ?
                    </span>
                    <span className="staffprofile-tooltip-bubble" role="tooltip">
                      {WEEKLY_HOUR_LIMIT_TOOLTIP}
                    </span>
                  </span>
                </label>
                <input
                  id="weeklyHourLimit"
                  type="number"
                  min="0"
                  max="168"
                  className="staffprofile-input"
                  value={form.weeklyHourLimit}
                  onChange={handleField("weeklyHourLimit")}
                  placeholder="optional"
                  aria-invalid={Boolean(fieldErrors.weeklyHourLimit)}
                />
                {fieldErrors.weeklyHourLimit && (
                  <p className="staffprofile-error-text">{fieldErrors.weeklyHourLimit}</p>
                )}
              </div>
            </div>
          </section>

          {/* Compensation — admin only (already gated above; defensive again here) */}
          {adminAccess && (
            <section
              className="staffprofile-card"
              aria-labelledby="compensation-heading"
              data-testid="compensation-section"
            >
              <h2 id="compensation-heading">Compensation</h2>

              {!isEdit ? (
                <div className="staffprofile-grid-2">
                  <div className="staffprofile-field">
                    <label htmlFor="hourlyRate">
                      Hourly rate<span aria-hidden="true">*</span>
                    </label>
                    <div className="staffprofile-currency-input">
                      <span aria-hidden="true">$</span>
                      <input
                        id="hourlyRate"
                        type="text"
                        inputMode="decimal"
                        className="staffprofile-input"
                        value={form.hourlyRate}
                        onChange={handleField("hourlyRate")}
                        placeholder="20.00"
                        aria-required="true"
                        aria-invalid={Boolean(fieldErrors.hourlyRate)}
                        aria-describedby="hourlyRate-preview"
                      />
                    </div>
                    {/* TIME-001-polish-ux-001 §AC-3: live $/hr preview
                        replaces the "Stored as cents on the server" helper
                        (implementation-detail leak). Catches the
                        "typed 2500 thinking cents" footgun by showing
                        the parsed currency live. */}
                    <p
                      id="hourlyRate-preview"
                      className="staffprofile-helper staffprofile-rate-preview"
                      data-testid="hourlyRate-preview"
                      aria-live="polite"
                    >
                      {liveHourlyRatePreview
                        ? `= ${liveHourlyRatePreview} per hour`
                        : "Type a dollar amount, e.g. 20.00"}
                    </p>
                    {fieldErrors.hourlyRate && (
                      <p className="staffprofile-error-text">{fieldErrors.hourlyRate}</p>
                    )}
                  </div>

                  <div className="staffprofile-field">
                    <label htmlFor="hourlyRateEffectiveDate">
                      Effective date<span aria-hidden="true">*</span>
                    </label>
                    <input
                      id="hourlyRateEffectiveDate"
                      type="date"
                      className="staffprofile-input"
                      value={form.hourlyRateEffectiveDate}
                      onChange={handleField("hourlyRateEffectiveDate")}
                      aria-required="true"
                      aria-invalid={Boolean(fieldErrors.hourlyRateEffectiveDate)}
                    />
                    {fieldErrors.hourlyRateEffectiveDate && (
                      <p className="staffprofile-error-text">
                        {fieldErrors.hourlyRateEffectiveDate}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="staffprofile-rate-display">
                    <div>
                      <span className="staffprofile-helper">Current rate</span>
                      <span className="staffprofile-rate-value">
                        {formatCentsAsCurrency(original?.hourlyRate) || "—"}
                      </span>
                      <span className="staffprofile-helper">
                        effective {toDateInputValue(original?.hourlyRateEffectiveDate) || "—"}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="staffprofile-btn staffprofile-btn-secondary"
                      onClick={openRateModal}
                    >
                      Edit rate
                    </button>
                  </div>

                  <div className="staffprofile-rate-history">
                    <h3>Rate history</h3>
                    {rateHistoryLoading ? (
                      <p className="staffprofile-helper">Loading history...</p>
                    ) : rateHistory.length === 0 ? (
                      <p className="staffprofile-helper">No prior rate changes recorded.</p>
                    ) : (
                      <ul>
                        {rateHistory.map((entry, idx) => {
                          const newRateCents =
                            entry.newValue ??
                            entry.metadata?.newRateCents ??
                            entry.after?.hourlyRate;
                          const ts = entry.createdAt || entry.timestamp;
                          const reason = entry.reason || entry.metadata?.reason;
                          return (
                            <li key={entry._id || entry.id || idx}>
                              <span className="staffprofile-rate-history-rate">
                                {formatCentsAsCurrency(newRateCents)}
                              </span>
                              <span className="staffprofile-helper">
                                {ts ? new Date(ts).toLocaleString() : ""}
                              </span>
                              {reason && (
                                <span className="staffprofile-rate-history-reason">
                                  {reason}
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </section>
          )}

          {/*
            TIME-001-polish-ux-001 §AC-4: Payroll vendor reference card hidden
            until QuickBooks Online (v2) ships. The `payrollVendorRef` field
            stays in the data model + payload pipeline (see handleSave —
            payload still ships an undefined/null value cleanly), so re-enabling
            this section when QBO arrives is a single-block uncomment with no
            backend change. Do NOT remove the field — admins who already
            populated it on existing profiles must not lose data.
          */}

          <div className="staffprofile-form-footer">
            {isEdit && status === "active" && (
              <button
                type="button"
                className="staffprofile-btn staffprofile-btn-danger"
                onClick={() => setDeactivateModalOpen(true)}
              >
                Deactivate
              </button>
            )}
            <div className="staffprofile-form-footer-spacer" />
            {/* TIME-001-polish-ux-001 §AC-7: Cancel-with-dirty-form guard.
                If the form has unsaved changes vs the pristine snapshot,
                we open the discard confirmation modal instead of navigating
                immediately. Clean form bypasses the prompt for parity with
                the prior silent-cancel behavior. */}
            <button
              type="button"
              className="staffprofile-btn staffprofile-btn-secondary"
              onClick={() => {
                if (isFormDirty()) {
                  setDiscardModalOpen(true);
                } else {
                  navigate("/staff/staff-profiles");
                }
              }}
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="staffprofile-btn staffprofile-btn-primary"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>

      {/* Rate change modal */}
      {rateModalOpen && (
        <div
          className="staffprofile-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rate-modal-heading"
        >
          <form className="staffprofile-modal-card" onSubmit={submitRateChange}>
            <h2 id="rate-modal-heading">Change hourly rate</h2>
            <p className="staffprofile-helper">
              Current rate {formatCentsAsCurrency(original?.hourlyRate)} effective{" "}
              {toDateInputValue(original?.hourlyRateEffectiveDate)}. The new rate
              is appended to the rate history; previous timesheets are not
              recomputed.
            </p>

            {rateModalError && (
              <div className="staffprofile-error" role="alert">
                {rateModalError}
              </div>
            )}

            <div className="staffprofile-field">
              <label htmlFor="rate-modal-rate">
                New rate<span aria-hidden="true">*</span>
              </label>
              <div className="staffprofile-currency-input">
                <span aria-hidden="true">$</span>
                <input
                  id="rate-modal-rate"
                  type="text"
                  inputMode="decimal"
                  className="staffprofile-input"
                  value={rateModalForm.newRate}
                  onChange={(e) =>
                    setRateModalForm((prev) => ({ ...prev, newRate: e.target.value }))
                  }
                  aria-required="true"
                />
              </div>
            </div>

            <div className="staffprofile-field">
              <label htmlFor="rate-modal-effective">
                Effective date<span aria-hidden="true">*</span>
              </label>
              <input
                id="rate-modal-effective"
                type="date"
                className="staffprofile-input"
                value={rateModalForm.effectiveDate}
                onChange={(e) =>
                  setRateModalForm((prev) => ({ ...prev, effectiveDate: e.target.value }))
                }
                aria-required="true"
              />
            </div>

            <div className="staffprofile-field">
              <label htmlFor="rate-modal-reason">
                Reason<span aria-hidden="true">*</span>
              </label>
              <textarea
                id="rate-modal-reason"
                className="staffprofile-input"
                rows={3}
                minLength={10}
                value={rateModalForm.reason}
                onChange={(e) =>
                  setRateModalForm((prev) => ({ ...prev, reason: e.target.value }))
                }
                aria-required="true"
                placeholder="At least 10 characters"
              />
            </div>

            <div className="staffprofile-modal-actions">
              <button
                type="button"
                className="staffprofile-btn staffprofile-btn-secondary"
                onClick={() => setRateModalOpen(false)}
                disabled={rateModalSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="staffprofile-btn staffprofile-btn-primary"
                disabled={rateModalSaving}
              >
                {rateModalSaving ? "Saving..." : "Save rate change"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* TIME-001-polish-ux-001 §AC-1: inline create-user modal */}
      {createUserModalOpen && (
        <div
          className="staffprofile-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="createuser-modal-heading"
        >
          <form
            className="staffprofile-modal-card"
            onSubmit={submitCreateUser}
            data-testid="createuser-modal"
          >
            <h2 id="createuser-modal-heading">Create new user account</h2>
            <p className="staffprofile-helper">
              The new user will receive an account-setup email. Once created,
              they will be auto-linked to this staff profile so you can finish
              the form without leaving the page.
            </p>

            {createUserError && (
              <div className="staffprofile-error" role="alert">
                {createUserError}
              </div>
            )}

            <div className="staffprofile-grid-2">
              <div className="staffprofile-field">
                <label htmlFor="createuser-firstName">
                  First name<span aria-hidden="true">*</span>
                </label>
                <input
                  id="createuser-firstName"
                  type="text"
                  className="staffprofile-input"
                  value={createUserForm.firstName}
                  onChange={(e) =>
                    setCreateUserForm((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                  aria-required="true"
                />
              </div>
              <div className="staffprofile-field">
                <label htmlFor="createuser-lastName">
                  Last name<span aria-hidden="true">*</span>
                </label>
                <input
                  id="createuser-lastName"
                  type="text"
                  className="staffprofile-input"
                  value={createUserForm.lastName}
                  onChange={(e) =>
                    setCreateUserForm((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                  aria-required="true"
                />
              </div>
            </div>

            <div className="staffprofile-grid-2">
              <div className="staffprofile-field">
                <label htmlFor="createuser-email">
                  Email<span aria-hidden="true">*</span>
                </label>
                <input
                  id="createuser-email"
                  type="email"
                  className="staffprofile-input"
                  value={createUserForm.email}
                  onChange={(e) =>
                    setCreateUserForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  aria-required="true"
                />
              </div>
              <div className="staffprofile-field">
                <label htmlFor="createuser-contactNumber">Contact phone</label>
                <input
                  id="createuser-contactNumber"
                  type="tel"
                  className="staffprofile-input"
                  value={createUserForm.contactNumber}
                  onChange={(e) =>
                    setCreateUserForm((prev) => ({
                      ...prev,
                      contactNumber: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="staffprofile-modal-actions">
              <button
                type="button"
                className="staffprofile-btn staffprofile-btn-secondary"
                onClick={() => setCreateUserModalOpen(false)}
                disabled={createUserSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="staffprofile-btn staffprofile-btn-primary"
                disabled={createUserSaving}
              >
                {createUserSaving ? "Creating..." : "Create and link"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* TIME-001-polish-ux-001 §AC-7: discard-unsaved-changes confirmation */}
      {discardModalOpen && (
        <div
          className="staffprofile-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="discard-modal-heading"
        >
          <div
            className="staffprofile-modal-card"
            data-testid="discard-modal"
          >
            <h2 id="discard-modal-heading">Discard unsaved changes?</h2>
            <p>
              You have unsaved changes. If you leave now, they will be lost.
            </p>
            <div className="staffprofile-modal-actions">
              <button
                type="button"
                className="staffprofile-btn staffprofile-btn-secondary"
                onClick={() => setDiscardModalOpen(false)}
                autoFocus
              >
                Stay
              </button>
              <button
                type="button"
                className="staffprofile-btn staffprofile-btn-danger"
                onClick={() => {
                  setDiscardModalOpen(false);
                  navigate("/staff/staff-profiles");
                }}
                data-testid="discard-confirm"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate confirmation modal */}
      {deactivateModalOpen && (
        <div
          className="staffprofile-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="deactivate-modal-heading"
        >
          <div className="staffprofile-modal-card">
            <h2 id="deactivate-modal-heading">
              Deactivate {form.firstName} {form.lastName}?
            </h2>
            <p>
              This staff profile will be marked Inactive. Historical time logs
              and audit entries are preserved. To re-enable, edit the profile
              and clear the inactive state.
            </p>
            {deactivateError && (
              <div className="staffprofile-error" role="alert">
                {deactivateError}
              </div>
            )}
            <div className="staffprofile-modal-actions">
              <button
                type="button"
                className="staffprofile-btn staffprofile-btn-secondary"
                onClick={() => setDeactivateModalOpen(false)}
                disabled={deactivateSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="staffprofile-btn staffprofile-btn-danger"
                onClick={submitDeactivate}
                disabled={deactivateSaving}
              >
                {deactivateSaving ? "Deactivating..." : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </StaffLayout>
  );
};

export default StaffProfileForm;
